// Landing's own hydrated auth control for the site header. Not a reuse of
// @bn-games/ui-shell's AuthWidget: that component is imperative-DOM, built
// for the other apps' persistent menu shell, and async — landing renders its
// whole page as one HTML string and prerenders that string at build time
// (scripts/prerender.mjs), so the header can only ever emit a static
// placeholder synchronously and hydrate it after mount, the same pattern
// main.ts's wireLangDropdown() already uses for the language switcher.

import { fetchMe, logout, openSignIn, avatarSrc, truncateName, classifyAuthState, BN_AUTH_CHANGE } from '@bn-games/auth-client';
import type { Profile } from '@bn-games/auth-client';
import { escapeHtml, escapeAttr } from '../html.js';
import { t } from '../i18n/I18n.js';

function renderUnauthenticated(container: HTMLElement, expanded: boolean): void {
  if (!expanded) {
    container.innerHTML = `<button class="header-auth__signin-btn" type="button">${escapeHtml(t('auth.signIn'))}</button>`;
    container.querySelector('button')?.addEventListener('click', () => {
      renderUnauthenticated(container, true);
    });
    return;
  }

  container.innerHTML = `
    <div class="header-auth__providers">
      <button class="header-auth__provider-btn header-auth__provider-btn--google" type="button" data-provider="google">${escapeHtml(t('auth.google'))}</button>
      <button class="header-auth__provider-btn header-auth__provider-btn--github" type="button" data-provider="github">${escapeHtml(t('auth.github'))}</button>
    </div>
  `;
  container.querySelectorAll<HTMLButtonElement>('[data-provider]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const provider = btn.dataset.provider as 'google' | 'github';
      void openSignIn(provider).finally(() => { void render(container); });
    });
  });
}

function renderIncomplete(container: HTMLElement): void {
  container.innerHTML = `
    <div class="header-auth__profile header-auth__profile--incomplete">
      <a class="header-auth__complete-link" href="/profile">${escapeHtml(t('auth.completeProfile'))}</a>
      <button class="header-auth__signout-btn" type="button">${escapeHtml(t('auth.signOut'))}</button>
    </div>
  `;
  container.querySelector('.header-auth__signout-btn')?.addEventListener('click', () => {
    void logout();
  });
}

function buildDropdown(): HTMLElement {
  const dropdown = document.createElement('div');
  dropdown.className = 'header-auth__dropdown';
  dropdown.innerHTML = `
    <a class="header-auth__dropdown-item" href="/profile">${escapeHtml(t('auth.profile'))}</a>
    <button class="header-auth__dropdown-item header-auth__dropdown-item--signout" type="button">${escapeHtml(t('auth.signOut'))}</button>
  `;
  dropdown.querySelector('button')?.addEventListener('click', () => {
    void logout();
  });
  return dropdown;
}

function renderAuthenticated(container: HTMLElement, profile: Profile & { handle: string }): void {
  const src = avatarSrc(profile.avatarUrl);
  const avatarAttr = src !== undefined ? ` src="${escapeAttr(src)}"` : '';

  container.innerHTML = `
    <div class="header-auth__profile" id="headerAuthProfile">
      <img class="header-auth__avatar"${avatarAttr} alt="${escapeAttr(profile.displayName)}" width="32" height="32" />
      <span class="header-auth__name">${escapeHtml(truncateName(profile.displayName))}</span>
    </div>
  `;

  const trigger = container.querySelector<HTMLElement>('#headerAuthProfile');
  trigger?.addEventListener('click', () => {
    const existing = container.querySelector('.header-auth__dropdown');
    if (existing !== null) {
      existing.remove();
      return;
    }
    container.appendChild(buildDropdown());
  });
}

async function render(container: HTMLElement): Promise<void> {
  container.innerHTML = '';
  const profile = await fetchMe();
  const state = classifyAuthState(profile);

  switch (state.kind) {
    case 'unauthenticated':
      renderUnauthenticated(container, false);
      return;
    case 'incomplete':
      renderIncomplete(container);
      return;
    case 'authenticated':
      renderAuthenticated(container, state.profile);
      return;
  }
}

export function hydrateHeaderAuth(): void {
  const container = document.querySelector<HTMLElement>('.header-auth');
  if (container === null) return;

  window.addEventListener(BN_AUTH_CHANGE, () => { void render(container); });
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Node) || !container.contains(e.target)) {
      container.querySelector('.header-auth__dropdown')?.remove();
    }
  });

  void render(container);
}
